import React from 'react'

import { useMember } from '@rewardops/auth/src/client'
import { useTenant } from '@rewardops/auth/src/client/use-tenant'
import { cn, LocalizedLink } from '@rewardops/ui'
import {
  AnalyticEvents,
  getItemCustomLabel,
  getItemPricing,
  type ResponseItem,
} from '@rewardops/utils'

import { useAnalytics } from '../hooks/use-analytics'
import { useMemberUUID } from '../hooks/use-member-uuid'
import { FavouritesWishlistHeart } from './components/favourite-wishlist-heart/favourite-wishlist-heart'
import PricingDetails from './components/item-card/pricing-details'
import { LabelBadge } from './components/label-badge'

interface ItemCardProps {
  item: ResponseItem
  isWishlist?: boolean
  position?: number
  listName?: string
}

const ItemCard: React.FC<ItemCardProps> = ({
  item,
  isWishlist,
  position,
  listName,
}: ItemCardProps) => {
  const tenant = useTenant()
  const analytics = useAnalytics()
  const memberUUID = useMemberUUID()

  const itemCardStyling = `bg-white grid grid-rows-[auto_1fr] md:h-[350px] h-[331px] cursor-pointer md:rounded-lg sm:rounded-none border border-zinc-200 hover:shadow-[0_3px_8px_0px_rgba(0,0,0,0.2)] ${
    isWishlist && 'md:rounded-b-none'
  }`

  const cashPrice =
    item?.supplier_items[0]?.retail_price[tenant.defaultCurrency] || 0
  const pricing = getItemPricing(item, tenant.defaultPoints)
  const customLabel = getItemCustomLabel(item)
  const member = useMember()

  const onSelectItem = () => {
    analytics.sendCustomGoogleAnalyticsEvent({
      type: AnalyticEvents.SelectItem,
      data: {
        points: pricing.price,
        member_id: memberUUID,
        index: Number(position ?? 0) + 1,
        item_list_name: listName ?? '',
        recommended: listName === 'recommended-items-carousel',
        items: [
          {
            item_id: item.id.toString(),
            item_name: item.name,
            item_brand: item?.brands[0]?.name,
            price: Number(cashPrice),
          },
        ],
      },
    })
  }

  return (
    <div className="relative">
      <div className="absolute right-0 top-0">
        <FavouritesWishlistHeart item={item} />
      </div>

      <LocalizedLink
        onClick={onSelectItem}
        className={cn(itemCardStyling)}
        href={`/item/${item.id}${
          listName ? `?listName=${encodeURIComponent(listName)}` : ''
        }`}
      >
        <div className="flex items-center justify-center px-6 pt-7">
          <div className="h-[156px] w-full lg:h-[180px] lg:w-[180px]">
            <img
              src={item.images[0]?.url ?? ''}
              alt={item.name}
              className="h-full w-full object-contain"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5 px-3 pb-6 pt-0 md:px-6 md:pt-6">
          <h3 className="line-clamp-2 text-base font-normal leading-6 text-gray-900">
            {item.name}
          </h3>
          <div className="space-y-1.5">
            <PricingDetails pricing={pricing} />
            {customLabel ? <LabelBadge label={customLabel} /> : null}
          </div>
        </div>
      </LocalizedLink>
    </div>
  )
}

export default ItemCard
